<template>
  <v-card class="wallet-connector">
    <v-card-title class="wallet-title">
      <v-icon color="primary" class="mr-2">mdi-wallet-outline</v-icon>
      Conectar Carteira
    </v-card-title>
    
    <!-- Tabs para escolher entre conectar ou criar carteira -->
    <v-tabs v-model="activeTab" color="primary" align-tabs="center" class="px-4">
      <v-tab value="connect">{{ t('wallet.connect') }}</v-tab>
      <v-tab value="create">Criar Nova Carteira</v-tab>
    </v-tabs>
    
    <v-card-text>
      <v-window v-model="activeTab">
        <!-- Tab para conectar carteira existente -->
        <v-window-item value="connect">
          <p class="wallet-intro-text mb-6">
            Conecte sua carteira para acessar a QuarnXSwap, incluindo trading cross-chain, gerenciamento de portfolio e funcionalidades de bridge.
          </p>
      
      <div class="wallet-options">
        <v-card 
          v-for="option in walletOptions" 
          :key="option.id"
          class="wallet-option"
          elevation="2"
          @click="connectWallet(option.id)"
          :color="option.id === 'quarnx' ? 'primary' : ''"
          :class="{ 'primary-wallet': option.id === 'quarnx' }"
        >
          <v-card-text class="wallet-option-content">
            <v-img 
              :src="option.image" 
              width="48" 
              height="48" 
              class="wallet-logo mx-auto mb-2"
            ></v-img>
            <div class="wallet-name text-center">{{ option.name }}</div>
            <div class="wallet-desc text-caption text-center mt-1">{{ option.description }}</div>
          </v-card-text>
        </v-card>
      </div>
      
      <div v-if="connectingWallet" class="wallet-connecting mt-4">
        <v-progress-circular
          indeterminate
          color="primary"
          size="24"
          class="mr-2"
        ></v-progress-circular>
        Conectando com {{ currentWallet.name }}...
      </div>
      
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        class="mt-4"
        closable
        @click:close="errorMessage = ''"
      >
        {{ errorMessage }}
      </v-alert>
      
      <v-alert
        v-if="!extensionInstalled && currentWallet?.id === 'quarnx'"
        type="info"
        variant="tonal"
        class="mt-4"
      >
        <div class="d-flex flex-column">
          <span>Extensão QuarnX Wallet não detectada.</span>
          <v-btn
            variant="text"
            color="primary"
            class="mt-2 align-self-start"
            @click="redirectToExtension"
          >
            <v-icon class="mr-1">mdi-download</v-icon>
            Instalar Extensão
          </v-btn>
        </div>
      </v-alert>
      
      <div class="mt-6 address-login-alternative">
        <v-divider class="mb-4">
          <span class="text-overline px-2">ou</span>
        </v-divider>
        
        <div class="manual-address">
          <v-text-field
            v-model="manualAddress"
            label="Endereço da Carteira QuarnX"
            placeholder="Exemplo: qx1a2b3c4d5e6f..."
            variant="outlined"
            clearable
            hint="Entre com seu endereço para acessar sem carteira"
            persistent-hint
          ></v-text-field>
          
          <v-btn
            block
            color="secondary"
            class="mt-4"
            @click="loginWithAddress"
            :disabled="!isValidAddress"
          >
            Acessar com Endereço
          </v-btn>
        </div>
      </div>
        </v-window-item>
        
        <!-- Tab para criar nova carteira -->
        <v-window-item value="create">
          <div class="create-wallet-container">
            <p class="wallet-intro-text mb-6">
              Crie uma nova carteira KinX segura para armazenar seus tokens e interagir com a QuarnXSwap.
            </p>
            
            <div v-if="createWalletStep === 1" class="text-center">
              <v-icon size="64" color="primary" class="mb-4">mdi-shield-key</v-icon>
              <h3 class="text-h6 mb-4">Criar Nova Carteira {{ coin }}</h3>
              <p class="mb-6">Ao criar uma carteira, você terá um endereço único na blockchain QuarnX e poderá armazenar {{ coin }} e outros tokens.</p>
              
              <v-alert type="warning" variant="tonal" class="mb-6">
                <div class="d-flex align-start">
                  <v-icon color="warning" class="mt-1 mr-2">mdi-alert</v-icon>
                  <div>
                    <p class="font-weight-bold mb-1">Importante: Guarde sua frase de recuperação!</p>
                    <p class="text-body-2">No próximo passo, você receberá uma frase mnemônica que é a ÚNICA forma de recuperar sua carteira. Anote-a em um local seguro e nunca compartilhe com ninguém!</p>
                  </div>
                </div>
              </v-alert>
              
              <v-btn color="primary" size="large" block @click="generateWallet">
                Gerar Carteira Segura
              </v-btn>
            </div>
            
            <div v-if="createWalletStep === 2" class="text-center">
              <v-icon size="48" color="warning" class="mb-4">mdi-shield-alert</v-icon>
              <h3 class="text-h6 mb-4">Sua Frase de Recuperação</h3>
              <p class="mb-6 text-body-2">Anote essas 12 palavras em ordem e guarde em local seguro. Elas são a única forma de recuperar sua carteira em caso de perda.</p>
              
              <v-card class="mnemonic-card mb-6" variant="outlined">
                <v-card-text>
                  <div class="mnemonic-container">
                    <div v-for="(word, index) in walletMnemonic" :key="index" class="mnemonic-word">
                      <span class="mnemonic-number">{{ index + 1 }}.</span> {{ word }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
              
              <v-checkbox v-model="mnemonicConfirmed" color="primary" hide-details class="mb-4">
                <template v-slot:label>
                  <div>Eu salvei minha frase de recuperação em um local seguro</div>
                </template>
              </v-checkbox>
              
              <v-btn color="primary" block :disabled="!mnemonicConfirmed" @click="createWalletStep = 3">
                Continuar
              </v-btn>
            </div>
            
            <div v-if="createWalletStep === 3" class="text-center">
              <v-icon size="48" color="success" class="mb-4">mdi-check-circle</v-icon>
              <h3 class="text-h6 mb-2">Carteira Criada com Sucesso!</h3>
              <p class="mb-6">Sua carteira {{ coin }} foi criada e está pronta para uso.</p>
              
              <v-card variant="outlined" class="mb-6">
                <v-card-text>
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-caption">Seu endereço:</span>
                    <v-chip variant="outlined" color="primary" size="small" class="copy-chip" @click="copyToClipboard(walletAddress)">
                      <v-icon size="small" start>mdi-content-copy</v-icon>
                      Copiar
                    </v-chip>
                  </div>
                  <div class="wallet-address mb-4">{{ walletAddress }}</div>
                  
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-caption">Chave privada:</span>
                    <v-chip variant="outlined" color="primary" size="small" class="copy-chip" @click="copyToClipboard(walletPrivateKey)">
                      <v-icon size="small" start>mdi-content-copy</v-icon>
                      Copiar
                    </v-chip>
                  </div>
                  <div class="wallet-private-key text-truncate">{{ walletPrivateKey }}</div>
                </v-card-text>
              </v-card>
              
              <v-alert type="warning" variant="tonal" class="mb-6">
                <p class="font-weight-bold">NUNCA compartilhe sua chave privada!</p>
                <p class="text-body-2">Quem tiver acesso à sua chave privada ou frase mnemônica terá controle total sobre sua carteira e fundos.</p>
              </v-alert>
              
              <v-btn color="primary" block @click="completeWalletCreation">
                Começar a Usar Minha Carteira
              </v-btn>
            </div>
          </div>
        </v-window-item>
      </v-window>
    </v-card-text>
    
    <v-card-actions class="wallet-footer">
      <v-btn
        variant="text"
        color="primary"
        @click="showFutureExtensionInfo = true"
      >
        Saiba mais sobre a carteira QuarnX
      </v-btn>
    </v-card-actions>
    
    <v-dialog v-model="showFutureExtensionInfo" max-width="500">
      <v-card>
        <v-card-title>QuarnX Wallet - Em Breve</v-card-title>
        <v-card-text>
          <p>A carteira QuarnX para navegadores (Chrome e Firefox) será a solução mais completa para interagir com o ecossistema QuarnX e outras blockchains populares.</p>
          
          <v-divider class="my-4"></v-divider>
          
          <h3 class="text-subtitle-1 mb-2">Principais Funcionalidades:</h3>
          <ul>
            <li>Gerenciamento de ativos em múltiplas blockchains</li>
            <li>Envio e recebimento de {{ coin }} e tokens de outras redes</li>
            <li>Acesso direto à QuarnXSwap</li>
            <li>Integração com DApps via provedor Web3</li>
            <li>Backup e recuperação segura</li>
            <li>Gerenciamento avançado de chaves</li>
          </ul>
          
          <v-divider class="my-4"></v-divider>
          
          <p class="text-caption">Assine nossa newsletter para ser notificado quando a extensão estiver disponível.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showFutureExtensionInfo = false">
            Entendi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { useDexStore } from '@/stores/dex'
import { useAppStore } from '@/stores/app'
import { API_BASE_URL } from '@/services/api'

export default {
  name: 'WalletConnector',
  
  data() {
    return {
      activeTab: 'connect',
      createWalletStep: 1,
      walletMnemonic: [],
      mnemonicConfirmed: false,
      walletAddress: '',
      walletPrivateKey: '',
      walletOptions: [
        {
          id: 'quarnx',
          name: 'QuarnX Wallet',
          description: 'Carteira oficial QuarnX com suporte multi-chain',
          image: '/assets/wallets/quarnx-wallet.svg'
        },
        {
          id: 'metamask',
          name: 'MetaMask',
          description: 'Conectar via MetaMask (Ethereum)',
          image: '/assets/wallets/metamask.svg'
        },
        {
          id: 'walletconnect',
          name: 'WalletConnect',
          description: 'Conectar com qualquer carteira compatível',
          image: '/assets/wallets/walletconnect.svg'
        }
      ],
      connectingWallet: false,
      errorMessage: '',
      currentWallet: null,
      extensionInstalled: false,
      manualAddress: '',
      showFutureExtensionInfo: false,
      isValidatingAddress: false
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    appStore() {
      return useAppStore()
    },
    isValidAddress() {
      // Validação de endereço QuarnX (hex de 32 bytes ou formato qx)
      if (!this.manualAddress) return false
      if (this.manualAddress.startsWith('qx') && this.manualAddress.length >= 26) return true
      // Also accept 64-char hex addresses (32 bytes)
      return /^[0-9a-fA-F]{64}$/.test(this.manualAddress)
    }
  },
  
  methods: {
    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
        .then(() => {
          this.appStore.addNotification({
            type: 'success',
            message: 'Copiado para a área de transferência!',
            timeout: 2000
          })
        })
        .catch(err => {
          console.error('Erro ao copiar para área de transferência:', err)
        })
    },
    
    generateWallet() {
      // B1+B2: Real Ed25519 keypair generation via Web Crypto API
      this.generateWalletAsync()
    },

    async generateWalletAsync() {
      try {
        const { generateKeypair, registerWallet } = await import('@/services/walletCrypto')
        const keypair = await generateKeypair()

        this.walletAddress = keypair.address
        this.walletPrivateKey = keypair.publicKeyHex.slice(0, 64)
        this.walletMnemonic = this.generateMnemonicFromBytes(keypair.privateKey.slice(0, 16))

        // B3: Register pubkey on kernel
        await registerWallet(keypair.address, keypair.publicKeyHex)

        // Request faucet for new wallet
        try {
          await fetch(`${API_BASE_URL}/api/v1/faucet`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address: keypair.address }),
          })
        } catch (e) {
          console.warn('Faucet request failed:', e)
        }

        // B5: Persist keypair in localStorage
        localStorage.setItem('quarnx_wallet', JSON.stringify({
          address: keypair.address,
          publicKeyHex: keypair.publicKeyHex,
        }))

        this.createWalletStep = 2
      } catch (error) {
        console.error('Erro ao gerar carteira:', error)
        this.errorMessage = 'Erro ao gerar carteira: ' + error.message
      }
    },

    /**
     * Generates a 12-word mnemonic from raw bytes (for display purposes).
     */
    generateMnemonicFromBytes(bytes) {
      const wordList = [
        'abandono', 'aberto', 'acaso', 'aceitar', 'acolhida', 'acordar',
        'acumular', 'adaptar', 'adega', 'admirar', 'admitir', 'adorar',
        'adquirir', 'adulto', 'adubo', 'adverso', 'afastar', 'afeto',
        'afinar', 'agente', 'agitado', 'agora', 'agosto', 'agradar',
        'agreste', 'agricultura', 'aguardar', 'agulha', 'ajuda', 'alarme',
        'alerta', 'alfabeto', 'alfinete', 'algum', 'aliar', 'alimento',
        'aliviar', 'almofada', 'altitude', 'aluno', 'amarelo', 'ambiente',
        'amigo', 'amizade', 'amor', 'amparo', 'amplo', 'analisar',
        'ancião', 'andorinha', 'anexo', 'angustia', 'animal', 'anjo',
        'ansiedade', 'antena', 'anterior', 'antigamente', 'antigo', 'anunciar',
        'anzol', 'apagar', 'aparecer', 'apertado', 'apesar', 'aplaudir'
      ]
      const selected = []
      for (let i = 0; i < 12; i++) {
        const idx = (bytes[i % bytes.length] || 0) % wordList.length
        selected.push(wordList[idx])
      }
      return selected
    },
    
    completeWalletCreation() {
      // Conectar com a carteira recém-criada
      this.dexStore.connectWallet(this.walletAddress, 'quarnx')

      // Persist to localStorage
      localStorage.setItem('quarnx_wallet', JSON.stringify({
        address: this.walletAddress,
        publicKeyHex: this.walletPrivateKey, // stored from generation
      }))
      
      // Adicionar notificação
      this.appStore.addNotification({
        type: 'success',
        message: 'Carteira KinX criada e conectada com sucesso!',
        timeout: 5000
      })
      
      // Emitir evento para o componente pai
      this.$emit('wallet-connected', this.walletAddress)
    },
    
    async connectWallet(walletId) {
      this.connectingWallet = true
      this.errorMessage = ''
      this.currentWallet = this.walletOptions.find(w => w.id === walletId)
      
      try {
        // Detecção da extensão QuarnX Wallet
        if (walletId === 'quarnx') {
          this.extensionInstalled = !!window.quarnxWallet
          
          if (!this.extensionInstalled) {
            this.connectingWallet = false
            return
          }
        }
        
        // Conectar ao kernel real
        const resp = await fetch(`${API_BASE_URL}/health`)
        const data = await resp.json()
        if (data.status !== 'ok') throw new Error('Kernel offline')
        
        // B2: Generate real Ed25519 keypair
        const { generateKeypair, registerWallet } = await import('@/services/walletCrypto')
        const keypair = await generateKeypair()
        const address = keypair.address

        // B3: Register on kernel
        await registerWallet(address, keypair.publicKeyHex)
        
        // Request faucet
        try {
          await fetch(`${API_BASE_URL}/api/v1/faucet`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address })
          })
        } catch (e) {
          console.warn('Faucet request failed:', e)
        }

        // B5: Persist to localStorage
        localStorage.setItem('quarnx_wallet', JSON.stringify({
          address: address,
          publicKeyHex: keypair.publicKeyHex,
        }))
        
        // Conectar com o Dex Store
        this.dexStore.connectWallet(address, walletId)
        
        // Adicionar notificação
        this.appStore.addNotification({
          type: 'success',
          message: `Carteira conectada com sucesso: ${this.shortenAddress(address)}`,
          timeout: 5000
        })
        
        // Emitir evento para o componente pai
        this.$emit('wallet-connected', address)
      } catch (error) {
        console.error('Erro ao conectar carteira:', error)
        this.errorMessage = 'Não foi possível conectar ao kernel QuarnX'
      } finally {
        this.connectingWallet = false
      }
    },
    
    async loginWithAddress() {
      if (!this.isValidAddress) return
      
      this.isValidatingAddress = true
      try {
        // Validar conexão com o kernel
        const resp = await fetch(`${API_BASE_URL}/health`)
        const data = await resp.json()
        if (data.status !== 'ok') throw new Error('Kernel offline')
        
        // Conectar com o endereço informado
        this.dexStore.connectWallet(this.manualAddress, 'manual')
        
        // Adicionar notificação
        this.appStore.addNotification({
          type: 'success',
          message: `Conectado como: ${this.shortenAddress(this.manualAddress)}`,
          timeout: 5000
        })
        
        // Emitir evento para o componente pai
        this.$emit('wallet-connected', this.manualAddress)
      } catch (error) {
        console.error('Erro ao fazer login com endereço:', error)
        this.errorMessage = 'Não foi possível conectar ao kernel QuarnX'
      } finally {
        this.isValidatingAddress = false
      }
    },
    
    redirectToExtension() {
      // Simulando redirecionamento para página de download da extensão
      window.open('https://quarnx.org/wallet-extension', '_blank')
    },
    
    shortenAddress(address) {
      if (!address) return ''
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
    }
  },
  
  mounted() {
    // Verificar se a extensão da carteira QuarnX está instalada
    this.extensionInstalled = !!window.quarnxWallet

    // B5: Restore wallet from localStorage
    try {
      const saved = localStorage.getItem('quarnx_wallet')
      if (saved) {
        const wallet = JSON.parse(saved)
        if (wallet.address) {
          this.walletAddress = wallet.address
        }
      }
    } catch {
      // ignore parse errors
    }
  }
}
</script>

<style scoped>
.wallet-connector {
  max-width: 600px;
  margin: 0 auto;
}

.wallet-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.wallet-intro-text {
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.wallet-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.wallet-option {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
}

.wallet-option:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.wallet-option-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
}

.primary-wallet {
  border: 2px solid;
}

.wallet-name {
  font-weight: 600;
  margin-top: 8px;
}

.address-login-alternative {
  margin-top: 24px;
}

/* Estilos para criação de carteira */
.create-wallet-container {
  padding: 16px 0;
}

.mnemonic-card {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.mnemonic-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 8px;
}

.mnemonic-word {
  padding: 8px;
  background-color: rgba(var(--v-theme-surface-variant), 0.1);
  border-radius: 4px;
  text-align: left;
}

.mnemonic-number {
  color: rgba(var(--v-theme-primary), 0.8);
  font-weight: 600;
  margin-right: 4px;
}

.wallet-address,
.wallet-private-key {
  padding: 8px 12px;
  background-color: rgba(var(--v-theme-surface-variant), 0.1);
  border-radius: 4px;
  word-break: break-all;
  font-family: monospace;
  font-size: 0.875rem;
}

.copy-chip {
  cursor: pointer;
}

.copy-chip:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}
</style>
